import React, { useState } from 'react';
import { ArrowLeft, FlowArrow, Table, Database } from '@phosphor-icons/react';
import { useTranslation } from '../i18n/LanguageContext';
import AdminPipeline from './AdminPipeline';
import AdminExplorer from './AdminExplorer';
import AdminDataManagement from './AdminDataManagement';

const MODULES = [
  {
    id: 'pipeline',
    title: 'Live Data Pipeline',
    description: 'Manage Azure OneLake connections, explore data models, and view ML models.',
    icon: FlowArrow,
    color: 'from-emerald-500/20 to-teal-500/10',
    borderColor: 'border-emerald-500/30',
    iconColor: 'text-emerald-600',
  },
  {
    id: 'explorer',
    title: 'Semantic Data Explorer',
    description: 'Explore database tables powering analytics, tiles, and charts.',
    icon: Table,
    color: 'from-blue-500/20 to-indigo-500/10',
    borderColor: 'border-blue-500/30',
    iconColor: 'text-blue-600',
  },
  {
    id: 'management',
    title: 'Data Management Console',
    description: 'Semantic model, data dictionary, lineage, and OneLake connection management.',
    icon: Database,
    color: 'from-amber-500/20 to-orange-500/10',
    borderColor: 'border-amber-500/30',
    iconColor: 'text-amber-600',
  },
];

export default function AdminConsole() {
  const { t } = useTranslation();
  const [activeModule, setActiveModule] = useState(null);

  if (activeModule === 'pipeline') return <AdminPipeline onBack={() => setActiveModule(null)} />;
  if (activeModule === 'explorer') return <AdminExplorer onBack={() => setActiveModule(null)} />;
  if (activeModule === 'management') return <AdminDataManagement onBack={() => setActiveModule(null)} />;

  return (
    <div className="space-y-6" data-testid="admin-console">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-text-primary tracking-tight" data-testid="admin-title">
          {t('admin.title')}
        </h1>
        <p className="font-body text-sm text-text-secondary mt-1">{t('admin.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {MODULES.map((mod) => {
          const Icon = mod.icon;
          return (
            <button
              key={mod.id}
              data-testid={`admin-module-${mod.id}`}
              onClick={() => setActiveModule(mod.id)}
              className={`group relative p-6 bg-gradient-to-br ${mod.color} border ${mod.borderColor} rounded-xl text-left transition-all duration-300 hover:shadow-lg hover:scale-[1.02] hover:border-primary/50`}
            >
              <div className={`w-12 h-12 rounded-xl bg-surface border border-border flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                <Icon size={24} weight="duotone" className={mod.iconColor} />
              </div>
              <h2 className="font-heading text-lg font-bold text-text-primary mb-2">{mod.title}</h2>
              <p className="font-body text-sm text-text-secondary leading-relaxed">{mod.description}</p>
              <div className="mt-4 flex items-center gap-1.5 text-primary font-body text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                Open module <ArrowLeft size={12} weight="bold" className="rotate-180" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
